import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

// Create/edit the sections of one class for one academic year.
// Renaming a section carries its students along (backend handles it);
// deleting is refused while students are still assigned.
const SectionManagerModal = ({ apiCall, year, className, onClose, onChanged, showSuccess, showError }) => {
  const [rows, setRows] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [newSection, setNewSection] = useState({ code: '', teacherId: '' });

  const load = useCallback(async () => {
    try {
      const [secs, tchs] = await Promise.all([
        apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`),
        apiCall('/academic/teachers')
      ]);
      setRows(secs);
      setTeachers(tchs);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, showError]);

  useEffect(() => { load(); }, [load]);

  const setField = (id, field, value) =>
    setRows(rs => rs.map(r => r.SectionID === id ? { ...r, [field]: value } : r));

  const handleAdd = async () => {
    if (!newSection.code.trim()) { showError('Section code is required (e.g. A)'); return; }
    try {
      await apiCall('/academic/sections', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: year, className,
          sectionCode: newSection.code,
          homeroomTeacherUserId: newSection.teacherId ? parseInt(newSection.teacherId) : null
        })
      });
      showSuccess(`Section ${className} ${newSection.code.toUpperCase()} created`);
      setNewSection({ code: '', teacherId: '' });
      await load();
      onChanged?.();
    } catch (err) { showError(err.message); }
  };

  const handleSave = async (row) => {
    try {
      await apiCall(`/academic/sections/${row.SectionID}`, {
        method: 'PUT',
        body: JSON.stringify({
          sectionCode: row.SectionCode,
          homeroomTeacherUserId: row.HomeroomTeacherUserID || null,
          isActive: !!row.IsActive
        })
      });
      showSuccess('Section updated');
      await load();
      onChanged?.();
    } catch (err) { showError(err.message); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete section ${className} ${row.SectionCode}?`)) return;
    try {
      await apiCall(`/academic/sections/${row.SectionID}`, { method: 'DELETE' });
      showSuccess('Section deleted');
      await load();
      onChanged?.();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[85vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Sections — {className} · {year}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Add */}
        <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Code</label>
            <input
              type="text" value={newSection.code} maxLength={10}
              onChange={e => setNewSection(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="A"
              className="w-20 border rounded-lg px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs text-gray-500 mb-1">Homeroom teacher (optional)</label>
            <select
              value={newSection.teacherId}
              onChange={e => setNewSection(f => ({ ...f, teacherId: e.target.value }))}
              className="w-full border rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">— none —</option>
              {teachers.map(t => <option key={t.UserID} value={t.UserID}>{t.FullName}</option>)}
            </select>
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {/* Existing sections */}
        <div className="space-y-1.5">
          {rows.map(row => (
            <div key={row.SectionID} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${row.IsActive ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`}>
              <input
                type="text" value={row.SectionCode} maxLength={10}
                onChange={e => setField(row.SectionID, 'SectionCode', e.target.value.toUpperCase())}
                className="w-16 border rounded px-2 py-1 text-sm"
              />
              <select
                value={row.HomeroomTeacherUserID || ''}
                onChange={e => setField(row.SectionID, 'HomeroomTeacherUserID', e.target.value ? parseInt(e.target.value) : null)}
                className="flex-1 border rounded px-2 py-1 text-sm"
              >
                <option value="">— no homeroom —</option>
                {teachers.map(t => <option key={t.UserID} value={t.UserID}>{t.FullName}</option>)}
              </select>
              <span className="text-xs text-gray-500 whitespace-nowrap">{row.StudentCount} students</span>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={!!row.IsActive}
                  onChange={e => setField(row.SectionID, 'IsActive', e.target.checked)} />
                active
              </label>
              <button onClick={() => handleSave(row)} className="text-blue-600 text-xs hover:underline">Save</button>
              <button onClick={() => handleDelete(row)} className="text-red-600 hover:text-red-800" title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-sm text-gray-500 italic text-center py-3">
              No sections yet for {className} — add the first one above (e.g. A, B).
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionManagerModal;
